import { mediaXsOnly } from 'cadenza/utils/media';
import { cadenzaSessionStorage } from 'cadenza/utils/storage/storage';
import type { DisplayState } from 'cadenza/api-client/workbook-api';
import { isPanelVisible } from 'cadenza/workbook/state/workbook-panel.state';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { assertUnreachable } from 'cadenza/utils/custom-error';

import type { Draft } from '@reduxjs/toolkit';

// Note: Exporting the names from the individual panel's module
// would lead to cyclic module dependencies.
export const WorkbookPanelName = {
  ANALYSIS_CONTEXT: 'd-analysis-context-panel',
  VIEW_SETTINGS: 'd-view-settings-panel', // Currently called designer
  DATA_BROWSER: 'd-data-browser-panel'
} as const;

export type WorkbookPanelName = typeof WorkbookPanelName[keyof typeof WorkbookPanelName];

const DISPLAY_STATE_PARAM = 'restoreDisplayState';

/**
 * Moves panel visibility settings from the optional "panels" URL param
 * to sessionStorage and removes the param from the URL.
 */
export function initializePanelVisibility (panelsParameter: string) {
  // hide all sidepanels on small displays
  if (mediaXsOnly.matches) {
    Object.values(WorkbookPanelName).forEach(panelName => {
      cadenzaSessionStorage.setItem(panelName, 'false');
    });
  }
  const url = new URL(location.href);
  let panelsParam = url.searchParams.get(panelsParameter);
  if (panelsParam != null) {
    const panelsToShow = panelsParam.split(',');
    Object.values(WorkbookPanelName).forEach(panelName => {
      cadenzaSessionStorage.setItem(panelName, String(panelsToShow.includes(panelName)));
    });
    url.searchParams.delete(panelsParameter);
    history.replaceState(null, '', url.toString());
  }
}

export function getPanelVisibilityFromDisplayState (displayState?: DisplayState) {
  const url = new URL(location.href);
  if (displayState && !mediaXsOnly.matches && url.searchParams.get(DISPLAY_STATE_PARAM) === 'true') {
    url.searchParams.delete(DISPLAY_STATE_PARAM);
    history.replaceState(null, '', url);
    return {
      [WorkbookPanelName.ANALYSIS_CONTEXT]: displayState.isAnalysisContextDisplayed,
      [WorkbookPanelName.VIEW_SETTINGS]: displayState.isDesignerDisplayed,
      [WorkbookPanelName.DATA_BROWSER]: displayState.isDataBrowserDisplayed
    };
  }
}

export function isWorkbookFeatureForPanelAvailable (panelId: WorkbookPanelName, state: Draft<WorkbookState>) {
  switch (panelId) {
    case 'd-analysis-context-panel':
      return isPanelVisible(state, panelId) && isWorkbookFeatureAvailable('workbook-analysis-context', state);
    case 'd-view-settings-panel':
      return isPanelVisible(state, panelId) && isWorkbookFeatureAvailable('workbook-design', state);
    case 'd-data-browser-panel':
      return isPanelVisible(state, panelId) && isWorkbookFeatureAvailable('workbook-data-browser', state);
    default:
      assertUnreachable(panelId);
  }
}
